import {
  HttpContextToken,
  HttpEvent,
  HttpInterceptorFn,
  HttpResponse,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { finalize, share, tap } from 'rxjs/operators';

/**
 * Duplicate-submit guard for every mutating request the app makes.
 *
 * Prod incident 2026-09-18: one vendor invoice was received four times
 * (GRN-RRWH01-864..867) — on a flaky link the user pressed Submit again each
 * time nothing came back, and every press opened a fresh POST. Nothing in
 * the app remembered the first attempt.
 *
 * Two layers, both here so no form needs its own code:
 *
 * 1. In-flight sharing. A second identical request (same method + URL +
 *    body) that starts while the first is still running does NOT leave the
 *    browser: it subscribes to the first one's response. Double-click safe.
 *
 * 2. `Idempotency-Key` header. The backend claims the key, runs the handler
 *    once, and replays the stored response to any retry with the same key
 *    (see backend src/common/idempotency). The key is derived from the
 *    request itself plus an "epoch" token, held per endpoint, that rotates
 *    after every successful mutation to that endpoint. So within one tab:
 *      - resubmitting the same form after a dropped connection → same key →
 *        the server replays (or refuses with 409 while the first still runs)
 *      - a genuinely new document after a success → new epoch → new key
 *    The epochs live in sessionStorage, so a reload keeps them. Per endpoint,
 *    not global: a PO saved in one tab must not re-key a GRN whose first
 *    attempt is still unanswered in another.
 *
 * Opt-outs: read-only POSTs (reports, exports, previews, validators, prints,
 * login, search) never carry a key — replaying those would return stale
 * data. A caller that sets its own `Idempotency-Key` (N deliberately
 * identical parallel creates, e.g. box labels) is passed through untouched.
 * `SKIP_IDEMPOTENCY` in the request context opts out per call.
 */
export const SKIP_IDEMPOTENCY = new HttpContextToken<boolean>(() => false);
export const IDEMPOTENCY_HEADER = 'Idempotency-Key';

const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Mutating verbs on URLs that do not create or change anything. Mirrors the
 *  backend's @SkipIdempotency() list; the server is the authority, this only
 *  avoids sending pointless keys. */
const READ_ONLY_URL =
  /\/(auth\/|report|export|preview|validate|parse-upload|filter-variants|search|quote|assessment|generate-next|label-zpl|print|gstin-validate|heartbeat|get-indent-ids|sync\/)/i;

const EPOCH_STORAGE_KEY = 'idempotency-epochs';
const MAX_EPOCHS = 64;

const inFlight = new Map<string, Observable<HttpEvent<unknown>>>();

function randomToken(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c?.randomUUID) return c.randomUUID().replace(/-/g, '');
  return (
    Date.now().toString(36) +
    Math.random().toString(36).slice(2, 12) +
    Math.random().toString(36).slice(2, 12)
  );
}

/** Fresh key for callers that must send N distinct identical requests. */
export function randomIdempotencyKey(): string {
  return `x-${randomToken()}`;
}

let epochs: Record<string, string> | null = null;

function loadEpochs(): Record<string, string> {
  if (epochs) return epochs;
  try {
    epochs = JSON.parse(sessionStorage.getItem(EPOCH_STORAGE_KEY) || '{}');
  } catch {
    epochs = {}; /* storage unavailable: in-memory epochs only */
  }
  return (epochs ??= {});
}

function saveEpochs(): void {
  try {
    sessionStorage.setItem(EPOCH_STORAGE_KEY, JSON.stringify(epochs));
  } catch {
    /* ignore */
  }
}

/** Epoch for one endpoint (method + URL), created on first use. */
function epochFor(scope: string): string {
  return loadEpochs()[scope] ?? rotateEpoch(scope);
}

function rotateEpoch(scope: string): string {
  const e = loadEpochs();
  delete e[scope]; // re-insert at the end = most recently used
  e[scope] = randomToken().slice(0, 16);
  const keys = Object.keys(e);
  if (keys.length > MAX_EPOCHS) delete e[keys[0]];
  saveEpochs();
  return e[scope];
}

// ── Request fingerprint: MUST match backend request-fingerprint.ts ──────────

const ISO_DATETIME =
  /(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?(?:Z|[+-]\d{2}:\d{2})/g;

function stable(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return '[' + v.map(stable).join(',') + ']';
  const o = v as Record<string, unknown>;
  return (
    '{' +
    Object.keys(o)
      .sort()
      .map((k) => JSON.stringify(k) + ':' + stable(o[k]))
      .join(',') +
    '}'
  );
}

function canonicalJson(value: unknown): string {
  const text = JSON.stringify(value === undefined ? null : value);
  return stable(text === undefined ? null : JSON.parse(text));
}

/** cyrb53 (public domain), two seeds → 106 bits. Collisions are harmless:
 *  the server compares its own sha256 fingerprint and answers 422. */
function hash53(str: string, seed: number): string {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (
    (h2 >>> 0).toString(16).padStart(8, '0') +
    (h1 >>> 0).toString(16).padStart(8, '0')
  );
}

function requestHash(method: string, url: string, body: unknown): string {
  const material = `${method} ${url}\n${canonicalJson(body ?? null).replace(
    ISO_DATETIME,
    '$1',
  )}`;
  return hash53(material, 0) + hash53(material, 1);
}

function isHashableBody(body: unknown): boolean {
  if (body == null) return true;
  if (typeof body !== 'object') return true;
  if (typeof FormData !== 'undefined' && body instanceof FormData) return false;
  if (typeof Blob !== 'undefined' && body instanceof Blob) return false;
  if (body instanceof ArrayBuffer) return false;
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return false;
  }
  return true;
}

export const idempotencyInterceptor: HttpInterceptorFn = (req, next) => {
  if (!MUTATING.has(req.method)) return next(req);
  if (req.context.get(SKIP_IDEMPOTENCY)) return next(req);
  // The caller chose its own key: respect it, and never share in flight.
  if (req.headers.has(IDEMPOTENCY_HEADER)) return next(req);
  if (req.responseType !== 'json') return next(req);
  if (!isHashableBody(req.body)) return next(req);
  if (READ_ONLY_URL.test(req.url)) return next(req);

  const scope = `${req.method} ${req.urlWithParams}`;
  const key = `${epochFor(scope)}-${requestHash(req.method, req.urlWithParams, req.body)}`;

  const running = inFlight.get(key);
  if (running) return running;

  const shared = next(req.clone({ setHeaders: { [IDEMPOTENCY_HEADER]: key } })).pipe(
    tap({
      next: (event) => {
        // A mutation succeeded: anything identical to THIS endpoint from now
        // on is a NEW intent, not a retry of this one.
        if (event instanceof HttpResponse) rotateEpoch(scope);
      },
    }),
    finalize(() => inFlight.delete(key)),
    share(),
  );
  inFlight.set(key, shared);
  return shared;
};
